import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getFacilityTypeSupplementLine,
  mergeFacilityDetailsForTaxonomy,
} from "@utils/facilityUtils";
import { resolveCheckoutFacilityType } from "@utils/pricingFacilityContext";
import { readLeftoverUnpaidIdentity } from "@utils/leftoverUnpaidCheckout";
// Inline icons to avoid separate files
function CardIcon({
  width = 16,
  height = 16,
  color = "#E6FAFC",
}: {
  width?: number;
  height?: number;
  color?: string;
}) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.5 10C10.2239 10 10 10.2239 10 10.5C10 10.7761 10.2239 11 10.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H10.5ZM1 5.5C1 4.11929 2.11929 3 3.5 3H12.5C13.8807 3 15 4.11929 15 5.5V10.5C15 11.8807 13.8807 13 12.5 13H3.5C2.11929 13 1 11.8807 1 10.5V5.5ZM14 6V5.5C14 4.67157 13.3284 4 12.5 4H3.5C2.67157 4 2 4.67157 2 5.5V6H14ZM2 7V10.5C2 11.3284 2.67157 12 3.5 12H12.5C13.3284 12 14 11.3284 14 10.5V7H2Z"
        fill={color}
      />
    </svg>
  );
}

function CheckCircleIcon({
  width = 16,
  height = 16,
  color = "#E6FAFC",
}: {
  width?: number;
  height?: number;
  color?: string;
}) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1ZM11.3536 6.35355L7.35355 10.3536C7.15829 10.5488 6.84171 10.5488 6.64645 10.3536L4.64645 8.35355C4.45118 8.15829 4.45118 7.84171 4.64645 7.64645C4.84171 7.45118 5.15829 7.45118 5.35355 7.64645L7 9.29289L10.6464 5.64645C10.8417 5.45118 11.1583 5.45118 11.3536 5.64645C11.5488 5.84171 11.5488 6.15829 11.3536 6.35355Z"
        fill={color}
      />
    </svg>
  );
}

interface OrderSummaryProps {
  planName: string;
  planPrice: number;
  currency?: string;
  period?: string;
  onCancel: () => void;
  onPayNow: (appliedCode?: string) => void;
  onApplyDiscount?: (
    code: string,
  ) => Promise<{ success: boolean; discountedPrice?: number; error?: string }>;
  isProcessing?: boolean;
  initialDiscountCode?: string; // Auto-apply discount code on mount
}

const Container = styled.div`
  border: 1px solid #cce7ee;
  border-radius: 8px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 600px;
  width: 100%;
  box-shadow: none;
`;

const Header = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #d8edf2;
  padding: 12px 14px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
`;

const HeaderTitle = styled.h2`
  font-family: "Rethink Sans", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #065b70;
  margin: 0;
  line-height: normal;
`;

const Content = styled.div`
  background: #ffffff;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-x: hidden;
  overflow-y: auto;
`;

const PlanCard = styled.div`
  background: #ffffff;
  padding: 0;
  border-radius: 0;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const PlanRow = styled.div<{ $hasBottomBorder?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: ${(props) => (props.$hasBottomBorder ? "1px solid #d8edf2" : "none")};
`;

const PlanLabel = styled.div<{ $width?: number }>`
  font-family: "Rethink Sans", sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #334155;
  line-height: 1.4;
  letter-spacing: -0.14px;
  padding: 2px 0;
  ${(props) => (props.$width ? `width: ${props.$width}px;` : "")}
`;

const PlanLabelBold = styled(PlanLabel)`
  font-weight: 600;
  flex: 1;
`;

const PlanValue = styled.div`
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PriceText = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
  letter-spacing: -0.14px;
`;

const PriceTextLarge = styled(PriceText)`
  font-size: 24px;
  letter-spacing: -0.2px;
`;

const PeriodText = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #64748b;
  line-height: 1.5;
  letter-spacing: -0.14px;
`;

const BillingAmountLine = styled.span`
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const DiscountToggle = styled.div<{ isExpanded: boolean }>`
  background: #ffffff;
  border: ${(props) =>
    props.isExpanded ? "1px solid #d8edf2" : "1px solid #d8edf2"};
  border-radius: 0;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s ease;
`;

const DiscountToggleHeader = styled.div`
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ToggleSwitch = styled.div<{ isOn: boolean }>`
  width: 28px;
  height: 18px;
  background: linear-gradient(
    180deg,
    rgba(38, 38, 38, 0.05) 40%,
    rgba(212, 212, 212, 0.05) 100%
  );
  border: 1px solid ${(props) => (props.isOn ? "#065b70" : "#737373")};
  border-radius: 100px;
  position: relative;
  transition: all 0.2s ease;
`;

const ToggleKnob = styled.div<{ isOn: boolean }>`
  width: 14px;
  height: 14px;
  background: ${(props) => (props.isOn ? "#065b70" : "#d4d4d4")};
  border-radius: 100px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: ${(props) => (props.isOn ? "2px" : "12px")};
  transition: all 0.2s ease;
`;

const ToggleText = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  color: #0f172a;
  line-height: 1.2;
  font-weight: 500;
`;

const DiscountContent = styled.div`
  background: #f8fdff;
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DiscountInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px;
`;

const DiscountInputLabel = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #065b70;
  line-height: 1.5;
  letter-spacing: -0.14px;
  padding: 6px 0;
  width: 153px;
`;

const DiscountInput = styled.input`
  flex: 1;
  background: #fcfeff;
  border: 1px solid #99d4e1;
  border-radius: 4px;
  padding: 4px 12px;
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  color: #065b70;
  outline: none;

  &:focus {
    border-color: #0a829d;
  }

  &::placeholder {
    color: #99d4e1;
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  background: linear-gradient(90deg, #0a829d 0%, #58c2d8 100%);
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.25);
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonText = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #e6fafc;
  line-height: normal;
`;

const TotalCostCard = styled.div`
  border-top: 1px solid #d8edf2;
  border-bottom: 1px solid #d8edf2;
  border-left: none;
  border-right: none;
  border-radius: 0;
  padding: 0;
`;

const TotalDueCard = styled.div`
  background: #eaf9fe;
  border: none;
  border-radius: 0;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const TotalDueTitle = styled.div`
  font-family: "Rethink Sans", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const TotalDueSubtext = styled.div`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #3f5562;
  margin-top: 2px;
`;

const TotalDueValue = styled.div`
  font-family: "Rethink Sans", sans-serif;
  font-size: 25px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1;
`;

const SectionHeading = styled.div`
  font-family: "Rethink Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #065b70;
  text-transform: uppercase;
  margin-top: 2px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 2px 0;
`;

const DetailKey = styled.div`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  color: #065b70;
`;

const DetailValue = styled.div`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  color: #065b70;
  font-weight: 600;
  text-align: right;
`;

const ChangePlanButton = styled.button`
  border: 1px solid #90d4e3;
  background: #e8f8fc;
  color: #065b70;
  font-family: "Rethink Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 8px;
  margin-left: 10px;
  border-radius: 12px;
  line-height: 18px;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #d8f0f7;
    border-color: #6fc1d6;
  }
`;

const MetaInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
`;

const MetaPair = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`;

const MetaLabel = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #065b70;
  line-height: 1.35;
`;

const MetaValue = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #065b70;
  line-height: 1.35;
  text-align: right;
  align-self: flex-end;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 10px 0 2px;
`;

const CancelButton = styled.button`
  background: #ffffff;
  border: 1px solid #b3b3b3;
  border-radius: 10px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
  }
`;

const PayNowButton = styled.button`
  background: linear-gradient(90deg, #0a829d 0%, #58c2d8 100%);
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.25);
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const CancelButtonText = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: normal;
`;

const PayNowButtonText = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #e6fafc;
  line-height: normal;
`;

const ErrorText = styled.span`
  font-family: "Rethink Sans", sans-serif;
  font-size: 12px;
  color: #dc2626;
  padding: 4px 0;
`;

export default function OrderSummary({
  planName,
  planPrice,
  currency = "$",
  period = "Per Facility per month",
  onCancel,
  onPayNow,
  onApplyDiscount,
  isProcessing = false,
  initialDiscountCode,
}: OrderSummaryProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // Expand discount toggle by default when initialDiscountCode is provided (from facility or URL)
  const hasInitialCode = !!(initialDiscountCode && String(initialDiscountCode).trim());
  const [isDiscountExpanded, setIsDiscountExpanded] = useState(hasInitialCode);
  const [discountCode, setDiscountCode] = useState(initialDiscountCode?.trim() || "");
  
  // Update discount code and expand toggle when initialDiscountCode changes
  useEffect(() => {
    if (initialDiscountCode && String(initialDiscountCode).trim()) {
      const code = String(initialDiscountCode).trim();
      setDiscountCode(code);
      setIsDiscountExpanded(true);
    }
  }, [initialDiscountCode]);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState<number | null>(null);
  const [isCodeValidated, setIsCodeValidated] = useState<boolean | null>(null);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string>("");

  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !onApplyDiscount) return;

    setIsApplyingDiscount(true);
    setDiscountError("");

    try {
      const result = await onApplyDiscount(discountCode.trim());

      if (result.success && result.discountedPrice !== undefined) {
        setDiscountedPrice(result.discountedPrice);
        setAppliedDiscountCode(discountCode.trim());
        setIsCodeValidated(true);
        // Keep UI expanded - don't collapse after applying
      } else {
        setDiscountError(result.error || "Invalid discount code");
        setIsCodeValidated(false);
        setAppliedDiscountCode("");
      }
    } catch (error) {
      setDiscountError("Failed to apply discount");
      setIsCodeValidated(false);
      setAppliedDiscountCode("");
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // When initialDiscountCode from facility/URL is present: expand section and auto-apply
  useEffect(() => {
    if (!initialDiscountCode || !String(initialDiscountCode).trim() || !onApplyDiscount) return;
    const code = String(initialDiscountCode).trim();
    setIsDiscountExpanded(true);
    setDiscountCode(code);
    // Auto-apply the promo code from the clicked facility
    let cancelled = false;
    onApplyDiscount(code)
      .then((result) => {
        if (cancelled) return;
        if (result.success && result.discountedPrice !== undefined) {
          setDiscountedPrice(result.discountedPrice);
          setAppliedDiscountCode(code);
          setIsCodeValidated(true);
        } else if (result.error) {
          setDiscountError(result.error);
        }
      })
      .catch(() => { if (!cancelled) setDiscountError("Failed to apply discount"); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDiscountCode]);

  const handleToggleDiscount = () => {
    setIsDiscountExpanded(!isDiscountExpanded);
    if (!isDiscountExpanded) {
      setDiscountError("");
    } else {
      setDiscountCode("");
      setDiscountError("");
      setDiscountedPrice(null);
      setIsCodeValidated(null);
      setAppliedDiscountCode("");
    }
  };


  // Reset validation state when user edits the code
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDiscountCode(value);
    setDiscountError("");
    // If user changes the code from the applied one, reset discount
    if (value.trim() !== appliedDiscountCode) {
      setDiscountedPrice(null);
      setIsCodeValidated(value.trim() === "" ? null : false);
    }
  };

  const displayPrice = discountedPrice !== null ? discountedPrice : planPrice;
  const hasDiscount = discountedPrice !== null && discountedPrice < planPrice;
  const discountAmount = hasDiscount ? Math.max(0, planPrice - displayPrice) : 0;
  const billingCycleLabel = (() => {
    const p = String(period || "").toLowerCase();
    // Detect annual variants first (e.g. "per facility per month (12 month term)")
    if (
      p.includes("annual") ||
      p.includes("year") ||
      p.includes("12 month term")
    ) {
      return "Annually";
    }
    if (p.includes("month")) {
      return "Monthly";
    }
    return "Annually";
  })();
  const monthlyPrice =
    billingCycleLabel === "Annually" ? planPrice / 12 : planPrice;
  const annualTotalPrice =
    billingCycleLabel === "Annually" ? planPrice : monthlyPrice * 12;
  const totalDueToday = displayPrice;
  const nextRenewalDate = (() => {
    const now = new Date();
    const next = new Date(now);
    if (billingCycleLabel === "Monthly") {
      next.setMonth(now.getMonth() + 1);
    } else {
      next.setFullYear(now.getFullYear() + 1);
    }
    return next.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  })();
  const formatAmount = (value: number) => Number(value || 0).toFixed(2);
  const formatWholeAmount = (value: number) =>
    Math.round(Number(value || 0)).toLocaleString();

  const accountMeta = (() => {
    try {
      const preferredRaw = sessionStorage.getItem("preferred_facility_for_pricing");
      const preferred = preferredRaw ? JSON.parse(preferredRaw) : null;
      const userRaw = localStorage.getItem("user");
      const user = userRaw ? JSON.parse(userRaw) : null;
      const mergedPreferred = mergeFacilityDetailsForTaxonomy(
        preferred as Record<string, unknown>,
      );
      return {
        facilityName:
          preferred?.facility_name ||
          preferred?.facility_details?.facility_name ||
          planName,
        facilityType:
          resolveCheckoutFacilityType(preferred, readLeftoverUnpaidIdentity()) ||
          preferred?.facility_type ||
          preferred?.facility_details?.facility_type ||
          "Facility",
        facilityTypeTaxonomy:
          getFacilityTypeSupplementLine(mergedPreferred),
        accountEmail: user?.email || "",
      };
    } catch {
      return {
        facilityName: planName,
        facilityType: "Facility",
        facilityTypeTaxonomy: "-",
        accountEmail: "",
      };
    }
  })();

  const handleChangePlan = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selected_plan_id");
    const query = params.toString();
    navigate(`/dashboard/pricing${query ? `?${query}` : ""}`);
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <HeaderTitle>Order Summary</HeaderTitle>
        </HeaderContent>
      </Header>

      <Content>
        <PlanCard>
          <PlanRow $hasBottomBorder>
            <MetaInfoRow>
              <MetaPair>
                <MetaLabel>Name</MetaLabel>
                <MetaLabel>Type</MetaLabel>
                <MetaLabel>Account</MetaLabel>
              </MetaPair>
              <MetaPair>
                <MetaValue>{accountMeta.facilityName}</MetaValue>
                <MetaValue>
                  <span style={{ display: "block" }}>{accountMeta.facilityType}</span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 11,
                      color: "#6b7280",
                      marginTop: 2,
                    }}
                  >
                    {accountMeta.facilityTypeTaxonomy}
                  </span>
                </MetaValue>
                <MetaValue>{accountMeta.accountEmail}</MetaValue>
              </MetaPair>
            </MetaInfoRow>
          </PlanRow>
          <PlanRow>
            <SectionHeading>Plan</SectionHeading>
          </PlanRow>
          <PlanRow $hasBottomBorder>
            <DetailRow style={{ width: "100%" }}>
              <DetailKey>Selected Plan</DetailKey>
              <DetailValue>
                {planName}
                <ChangePlanButton type="button" onClick={handleChangePlan}>
                  {" "}
                  ← Change
                </ChangePlanButton>
              </DetailValue>
            </DetailRow>
          </PlanRow>
          <PlanRow>
            <DetailRow style={{ width: "100%" }}>
              <DetailKey>Billing Cycle</DetailKey>
              <DetailValue>{billingCycleLabel}</DetailValue>
            </DetailRow>
          </PlanRow>
        </PlanCard>

        {/* Discount Toggle */}
        {onApplyDiscount && (
          <DiscountToggle isExpanded={isDiscountExpanded}>
            <DiscountToggleHeader onClick={handleToggleDiscount}>
              <ToggleSwitch isOn={isDiscountExpanded}>
                <ToggleKnob isOn={isDiscountExpanded} />
              </ToggleSwitch>
              <ToggleText>Apply Discount</ToggleText>
            </DiscountToggleHeader>

            {isDiscountExpanded && (
              <DiscountContent>
                <DiscountInputRow>
                  <DiscountInputLabel>Enter Discount Code:</DiscountInputLabel>
                  <DiscountInput
                    type="text"
                    value={discountCode}
                    onChange={handleCodeChange}
                    placeholder="Enter code"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleApplyDiscount();
                      }
                    }}
                  />
                </DiscountInputRow>

                {discountError && <ErrorText>{discountError}</ErrorText>}

                <ApplyButton
                  onClick={handleApplyDiscount}
                  disabled={
                    !discountCode.trim() ||
                    isApplyingDiscount ||
                    (isCodeValidated === true && discountCode.trim() === appliedDiscountCode)
                  }
                >
                  <CheckCircleIcon width={16} height={16} color="#e6fafc" />
                  <ButtonText>
                    {isApplyingDiscount
                      ? "Applying..."
                      : isCodeValidated === true && discountCode.trim() === appliedDiscountCode
                      ? "Applied"
                      : "Apply Discount"}
                  </ButtonText>
                </ApplyButton>
              </DiscountContent>
            )}
          </DiscountToggle>
        )}

        {/* Total Cost */}
        <TotalCostCard>
          <PlanCard>
            <PlanRow>
              <SectionHeading>Billing Summary</SectionHeading>
            </PlanRow>
            <PlanRow>
              <DetailRow style={{ width: "100%" }}>
                <DetailKey>Monthly rate</DetailKey>
                <DetailValue>
                  <BillingAmountLine>
                    {currency}
                    {formatAmount(monthlyPrice)}
                    <PeriodText> / month</PeriodText>
                  </BillingAmountLine>
                </DetailValue>
              </DetailRow>
            </PlanRow>
            <PlanRow $hasBottomBorder>
              <DetailRow style={{ width: "100%" }}>
                <DetailKey>Annual total</DetailKey>
                <DetailValue>
                  <BillingAmountLine>
                    {currency}
                    {formatAmount(annualTotalPrice)}
                    <PeriodText> / year</PeriodText>
                  </BillingAmountLine>
                </DetailValue>
              </DetailRow>
            </PlanRow>
            <PlanRow $hasBottomBorder>
              <DetailRow style={{ width: "100%" }}>
                <DetailKey>Next Renewal</DetailKey>
                <DetailValue>{nextRenewalDate}</DetailValue>
              </DetailRow>
            </PlanRow>
            {hasDiscount && (
              <PlanRow>
                <PlanLabelBold>Discounted Price</PlanLabelBold>
                <PlanValue>
                  <PriceTextLarge style={{ color: "#64748b", fontSize: "18px" }}>
                    {currency}
                    {formatAmount(discountAmount)}
                  </PriceTextLarge>
                </PlanValue>
              </PlanRow>
            )}

          </PlanCard>
        </TotalCostCard>

        <TotalDueCard>
          <div>
            <TotalDueTitle>Total due today</TotalDueTitle>
            <TotalDueSubtext>
              {billingCycleLabel === "Monthly"
                ? "Billed monthly. Cancel anytime before your renewal date."
                : "Billed annually. Cancel anytime before your renewal date."}
            </TotalDueSubtext>
          </div>
          <TotalDueValue>
            {currency}
            {formatWholeAmount(totalDueToday)}
          </TotalDueValue>
        </TotalDueCard>

        {/* Action Buttons */}
        <ButtonRow>
          <CancelButton onClick={onCancel}>
            <CancelButtonText>Back</CancelButtonText>
          </CancelButton>

          <PayNowButton
            onClick={() => onPayNow(discountCode || undefined)}
            disabled={
              isProcessing ||
              (Boolean(discountCode.trim()) && isCodeValidated !== true)
            }
          >
            {isProcessing ? (
              <PayNowButtonText>Processing...</PayNowButtonText>
            ) : (
              <>
                <CardIcon width={16} height={16} color="#e6fafc" />
                <PayNowButtonText>Pay Now</PayNowButtonText>
              </>
            )}
          </PayNowButton>
        </ButtonRow>
      </Content>
    </Container>
  );
}
